/**
 * postMessage protocol between the embeddable widget (host page) and the
 * `/embed` route running inside the overlay iframe.
 *
 * Both sides validate `event.origin` before trusting a message; these guards
 * only validate shape.
 */

export const PROTOCOL_VERSION = 1;

export type ThemeMode = 'light' | 'dark';

/** Sent once the frame reports ready, and again whenever host config changes. */
export interface EsInitMessage {
  type: 'es:init';
  version: number;
  query: string;
  filters: Record<string, string[]>;
  tokens: Record<string, string>;
  theme: ThemeMode;
  uiLanguage?: string;
  fontCssUrl?: string;
  overlayTitle?: string;
  overlayLogoUrl?: string;
  /** Host-origin search history, so the overlay's own bar shows the same list. */
  history: string[];
}

export interface EsQueryMessage {
  type: 'es:query';
  version: number;
  query: string;
  filters?: Record<string, string[]>;
}

export interface EsThemeMessage {
  type: 'es:theme';
  version: number;
  tokens: Record<string, string>;
  theme: ThemeMode;
}

export type HostToFrameMessage = EsInitMessage | EsQueryMessage | EsThemeMessage;

export interface EsReadyMessage {
  type: 'es:ready';
  version: number;
}

export interface EsCloseMessage {
  type: 'es:close';
  version: number;
}

export interface EsNavigateMessage {
  type: 'es:navigate';
  version: number;
  url: string;
  newTab?: boolean;
}

export interface EsHistoryAddMessage {
  type: 'es:history-add';
  version: number;
  query: string;
}

export type FrameToHostMessage =
  | EsReadyMessage
  | EsCloseMessage
  | EsNavigateMessage
  | EsHistoryAddMessage;

const FRAME_TO_HOST_TYPES = new Set([
  'es:ready',
  'es:close',
  'es:navigate',
  'es:history-add',
]);

const HOST_TO_FRAME_TYPES = new Set(['es:init', 'es:query', 'es:theme']);

function hasVersionedType(data: unknown): data is { type: string; version: number } {
  if (typeof data !== 'object' || data === null) return false;
  const candidate = data as { type?: unknown; version?: unknown };
  return typeof candidate.type === 'string' && candidate.version === PROTOCOL_VERSION;
}

export function isFrameToHostMessage(data: unknown): data is FrameToHostMessage {
  return hasVersionedType(data) && FRAME_TO_HOST_TYPES.has(data.type);
}

export function isHostToFrameMessage(data: unknown): data is HostToFrameMessage {
  return hasVersionedType(data) && HOST_TO_FRAME_TYPES.has(data.type);
}

/** Only http(s) may be opened from a frame-supplied URL — blocks `javascript:`. */
export function isSafeNavigationUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
